import { STATUS, StatusDot, type StatusKey } from "@/components/StatusIndicator";

/**
 * MilestoneTrack - 계획서 12 의 진행 트랙
 *
 *   원소재 ● -> 금형 ● -> T/O ● -> 생산 ◆ -> 검사 ○ -> 고객승인 ○ -> SOP ○
 *
 * 하나의 이어진 진행으로 읽혀야 한다. 그래서 노드를 따로 그리지
 * 않고, 끊기지 않는 2px 선 위에 노드를 얹는다. 각 구간의 색은
 * "그 구간을 지나온 노드"의 색이다. 결과적으로 색이 칠해진 길이가
 * 곧 진행한 거리로 읽힌다.
 *
 * 같은 컴포넌트가 두 배율로 쓰인다.
 *   size="row"  - 테이블 행 안. 10px 글리프, 라벨 없음, 약 132px.
 *   size="hero" - 상세화면 상단. 20px 글리프 + 라벨 + 계획/완료일.
 * DOM 순서, 색 규칙, 실루엣이 동일하므로 두 화면에서 같은 물건으로
 * 인식된다.
 */

export type Milestone = {
  key: string;
  /** 원소재, 금형, T/O … 화면에 그대로 나간다 */
  label: string;
  status: StatusKey;
  /** MM/DD */
  plan?: string;
  /** MM/DD. 없으면 "-" */
  actual?: string;
};

const SEG_IDLE = "var(--border-subtle)";

export const MilestoneTrack = ({
  items,
  currentKey,
  size = "row",
  className,
}: {
  items: Milestone[];
  /** 현재 진행 중인 노드. 헤일로가 붙는다 */
  currentKey?: string;
  size?: "row" | "hero";
  className?: string;
}) => {
  const hero = size === "hero";
  // 지나온 구간의 색. 미착수 노드는 길을 칠하지 않는다.
  const seg = (i: number) => {
    const it = items[i];
    if (!it || it.status === "idle") return SEG_IDLE;
    return STATUS[it.status].color;
  };

  return (
    <ol
      aria-label="개발 마일스톤 진행"
      className={[
        "flex list-none p-0 m-0",
        hero ? "items-start w-full min-w-[30rem]" : "items-center",
        className ?? "",
      ].join(" ")}
    >
      {items.map((m, i) => {
        const isCurrent = m.key === currentKey;
        const meta = STATUS[m.status];

        if (!hero) {
          // 행 배율: [구간][노드][구간][노드]… 한 줄.
          return (
            <li
              key={m.key}
              aria-current={isCurrent ? "step" : undefined}
              className="flex items-center"
            >
              {i > 0 && (
                <span
                  aria-hidden="true"
                  className="block h-[2px] w-s3 shrink-0"
                  style={{ background: seg(i - 1) }}
                />
              )}
              <span
                className="flex items-center justify-center rounded-pill p-[2px]"
                style={
                  isCurrent
                    ? {
                        // 표면색 간격 + 상태색 얇은 링. var() 문자열에는
                        // 알파를 이어붙일 수 없으므로 링으로 처리한다.
                        boxShadow: `0 0 0 1.5px var(--surface), 0 0 0 2.5px ${meta.color}`,
                        background: "var(--surface)",
                      }
                    : undefined
                }
              >
                <StatusDot
                  status={m.status}
                  size="row"
                  title={`${m.label} ${meta.label}${isCurrent ? " 진행 중" : ""}`}
                />
              </span>
            </li>
          );
        }

        // 히어로 배율: 각 항목이 자기 왼쪽 절반과 오른쪽 절반의
        // 선을 그린다. 그래서 선이 끊기지 않는다.
        return (
          <li
            key={m.key}
            aria-current={isCurrent ? "step" : undefined}
            className="relative flex flex-1 min-w-[4.25rem] flex-col items-center gap-s2 pt-s1"
          >
            {i > 0 && (
              <span
                aria-hidden="true"
                className="absolute left-0 top-[1.0625rem] h-[2px] w-1/2"
                style={{ background: seg(i - 1) }}
              />
            )}
            {i < items.length - 1 && (
              <span
                aria-hidden="true"
                className="absolute left-1/2 top-[1.0625rem] h-[2px] w-1/2"
                style={{ background: seg(i) }}
              />
            )}
            <span
              className="relative z-base flex items-center justify-center rounded-pill p-s1"
              style={{
                background: "var(--surface)",
                boxShadow: isCurrent ? `0 0 0 2px ${meta.color}` : undefined,
              }}
            >
              <StatusDot status={m.status} size="hero" labelled />
            </span>
            <span
              className="text-caption font-semibold text-center"
              style={{ color: isCurrent ? meta.color : "var(--text-secondary)" }}
            >
              {m.label}
            </span>
            <span className="sr-only">{meta.label}</span>
            {(m.plan || m.actual) && (
              <span
                className="text-micro text-center tabular-nums"
                style={{ color: "var(--text-tertiary)" }}
              >
                {m.plan ?? "-"}
                <br />
                {m.actual ?? "-"}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
};
